import { useState, useEffect } from 'react';
import {
  Send,
  Clock,
  ExternalLink,
  Home,
  Calendar,
  Wrench,
  MessageCircle,
  User,
  Users,
  HelpCircle,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Bell,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

interface AdminBroadcast {
  id: string;
  title: string;
  message: string;
  target_type: 'external_url' | 'internal_page';
  target_url: string | null;
  target_page: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  status: 'draft' | 'scheduled' | 'sent' | 'cancelled';
  recipient_count: number;
  created_at: string;
}

const internalPages = [
  { value: 'dashboard', label: 'Dashboard', icon: Home },
  { value: 'ssw', label: 'SSW-Tracker', icon: Calendar },
  { value: 'tools', label: 'Tools', icon: Wrench },
  { value: 'chat', label: 'KI-Chat', icon: MessageCircle },
  { value: 'profile', label: 'Profil', icon: User },
  { value: 'calendar', label: 'Kalender', icon: Calendar },
  { value: 'partner', label: 'Partner', icon: Users },
  { value: 'help', label: 'Hilfe', icon: HelpCircle },
];

// Rundet auf nächste halbe Stunde
function roundToNext30Minutes(date: Date): Date {
  const minutes = date.getMinutes();
  const roundedMinutes = minutes < 30 ? 30 : 60;
  const newDate = new Date(date);
  newDate.setMinutes(roundedMinutes === 60 ? 0 : roundedMinutes);
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);
  if (roundedMinutes === 60) {
    newDate.setHours(newDate.getHours() + 1);
  }
  return newDate;
}

// Generiert Zeitoptionen in 30-Minuten-Schritten
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      slots.push(`${hour}:${minute}`);
    }
  }
  return slots;
}

export function BroadcastPage() {
  // Form State
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetType, setTargetType] = useState<'external_url' | 'internal_page'>('internal_page');
  const [targetUrl, setTargetUrl] = useState('');
  const [targetPage, setTargetPage] = useState('dashboard');
  const [sendMode, setSendMode] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // List State
  const [broadcasts, setBroadcasts] = useState<AdminBroadcast[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Initialisierung
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduledDate(tomorrow.toISOString().split('T')[0]);

    const nextSlot = roundToNext30Minutes(now);
    const hours = nextSlot.getHours().toString().padStart(2, '0');
    const minutes = nextSlot.getMinutes().toString().padStart(2, '0');
    setScheduledTime(`${hours}:${minutes}`);

    fetchBroadcasts();
  }, []);

  const fetchBroadcasts = async () => {
    try {
      const { data, error } = await getSupabaseAdminClient()
        .from('admin_broadcasts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setBroadcasts(data || []);
    } catch (err) {
      console.error('Error fetching broadcasts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      alert('Bitte Titel und Nachricht eingeben');
      return;
    }

    if (targetType === 'external_url' && !targetUrl.trim()) {
      alert('Bitte eine URL eingeben');
      return;
    }

    setIsSending(true);

    try {
      // Broadcast erstellen
      const scheduledFor = sendMode === 'scheduled'
        ? new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString()
        : null;

      const { data: broadcast, error: insertError } = await getSupabaseAdminClient()
        .from('admin_broadcasts')
        .insert({
          title,
          message,
          target_type: targetType,
          target_url: targetType === 'external_url' ? targetUrl : null,
          target_page: targetType === 'internal_page' ? targetPage : null,
          scheduled_for: scheduledFor,
          status: sendMode === 'now' ? 'draft' : 'scheduled',
        } as never)
        .select()
        .single();

      if (insertError) throw insertError;

      // Wenn sofort senden, dann jetzt versenden
      if (sendMode === 'now') {
        const { data: count, error: sendError } = await getSupabaseAdminClient().rpc(
          'send_admin_broadcast',
          { p_broadcast_id: (broadcast as AdminBroadcast).id } as never
        );

        if (sendError) throw sendError;

        alert(`Broadcast erfolgreich an ${count} User gesendet!`);
      } else {
        alert('Broadcast wurde geplant und wird zum angegebenen Zeitpunkt gesendet.');
      }

      // Form zurücksetzen
      setTitle('');
      setMessage('');
      setTargetUrl('');
      setTargetPage('dashboard');
      setSendMode('now');

      // Liste aktualisieren
      fetchBroadcasts();
    } catch (err) {
      console.error('Error sending broadcast:', err);
      alert('Fehler beim Senden des Broadcasts');
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!confirm('Möchtest du diesen geplanten Broadcast wirklich abbrechen?')) return;

    try {
      const { error } = await getSupabaseAdminClient()
        .from('admin_broadcasts')
        .update({ status: 'cancelled' } as never)
        .eq('id', id);

      if (error) throw error;
      fetchBroadcasts();
    } catch (err) {
      console.error('Error cancelling broadcast:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Möchtest du diesen Broadcast wirklich löschen?')) return;

    try {
      const { error } = await getSupabaseAdminClient()
        .from('admin_broadcasts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchBroadcasts();
    } catch (err) {
      console.error('Error deleting broadcast:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle className="w-3 h-3" />
            Gesendet
          </span>
        );
      case 'scheduled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
            <Clock className="w-3 h-3" />
            Geplant
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
            <XCircle className="w-3 h-3" />
            Abgebrochen
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
            Entwurf
          </span>
        );
    }
  };

  const timeSlots = generateTimeSlots();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Broadcast senden</h1>
        <p className="text-gray-500 mt-1">
          Sende eine Benachrichtigung an alle User
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Neue Benachrichtigung</h2>

          <div className="space-y-4">
            {/* Titel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titel *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Neue Funktion verfügbar!"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                maxLength={100}
              />
            </div>

            {/* Nachricht */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nachricht *
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Die Nachricht, die alle User erhalten..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                maxLength={500}
              />
              <p className="text-xs text-gray-400 mt-1">{message.length}/500 Zeichen</p>
            </div>

            {/* Ziel-Typ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ziel beim Antippen
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTargetType('internal_page')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    targetType === 'internal_page'
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Home className="w-4 h-4 inline mr-2" />
                  Interne Seite
                </button>
                <button
                  type="button"
                  onClick={() => setTargetType('external_url')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    targetType === 'external_url'
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ExternalLink className="w-4 h-4 inline mr-2" />
                  Externe URL
                </button>
              </div>
            </div>

            {/* Ziel-Auswahl */}
            {targetType === 'internal_page' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seite auswählen
                </label>
                <select
                  value={targetPage}
                  onChange={(e) => setTargetPage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  {internalPages.map((page) => (
                    <option key={page.value} value={page.value}>
                      {page.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL eingeben
                </label>
                <input
                  type="url"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Zeitplanung */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wann senden?
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSendMode('now')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    sendMode === 'now'
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Send className="w-4 h-4 inline mr-2" />
                  Sofort
                </button>
                <button
                  type="button"
                  onClick={() => setSendMode('scheduled')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    sendMode === 'scheduled'
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Clock className="w-4 h-4 inline mr-2" />
                  Planen
                </button>
              </div>
            </div>

            {/* Datum & Zeit (wenn geplant) */}
            {sendMode === 'scheduled' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum
                  </label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uhrzeit
                  </label>
                  <select
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot} Uhr
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                disabled={!title || !message}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Vorschau
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={isSending || !title || !message}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline mr-2" />
                    Wird gesendet...
                  </>
                ) : sendMode === 'now' ? (
                  <>
                    <Send className="w-4 h-4 inline mr-2" />
                    Jetzt senden
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 inline mr-2" />
                    Planen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Vorschau */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vorschau</h3>

              {/* Simulated Notification */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Bell className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{title || 'Titel'}</p>
                    <p className="text-sm text-gray-600 mt-1">{message || 'Nachricht'}</p>
                    <p className="text-xs text-gray-400 mt-2">Gerade eben</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-500">
                <p>
                  <strong>Ziel:</strong>{' '}
                  {targetType === 'external_url'
                    ? targetUrl || '(keine URL)'
                    : internalPages.find((p) => p.value === targetPage)?.label || targetPage}
                </p>
              </div>

              <button
                onClick={() => setShowPreview(false)}
                className="w-full mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        )}

        {/* Historie */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Versandte Broadcasts</h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Send className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Noch keine Broadcasts gesendet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {broadcasts.map((broadcast) => (
                <div
                  key={broadcast.id}
                  className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{broadcast.title}</p>
                      <p className="text-sm text-gray-500 line-clamp-1">{broadcast.message}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        {getStatusBadge(broadcast.status)}
                        {broadcast.status === 'sent' && (
                          <span>{broadcast.recipient_count} Empfänger</span>
                        )}
                        {broadcast.status === 'scheduled' && broadcast.scheduled_for && (
                          <span>Geplant für {formatDate(broadcast.scheduled_for)}</span>
                        )}
                        {broadcast.sent_at && (
                          <span>Gesendet am {formatDate(broadcast.sent_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {broadcast.status === 'scheduled' && (
                        <button
                          onClick={() => handleCancel(broadcast.id)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          title="Abbrechen"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(broadcast.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
